import CustomTable from "components/CustomTable";
import { LoyaltyListToolbar, LoyaltyTableActions } from "features/LoyaltyPackage/presentation/components";
import {
  ProgramConfirmModal,
  ProgramFormModal,
  useProgramFormModal,
} from "features/LoyaltyPackage/presentation/pages/ProgramFormModal";
import { useLoyaltyPackage } from "features/LoyaltyPackage/data/hooks";
import { formatLoyaltyDateRangeMultiline } from "features/LoyaltyPackage/core/utils";
import { t } from "i18next";
import { Link } from "react-router-dom";
import { notify } from "utils/helperFuncs";
import useProgramList from "./useProgramList";

function ProgramList() {
  const { TEXT_STATUS_LOYALTY } = useLoyaltyPackage();

  const {
    searchTerm,
    handleSearch,
    programs,
    paginationData,
    loading,
    switchLoading,
    handleTableChange,
    handleSwitchStatus,
    isEditable,
    pagination,
    filters,
    refetch,
    listRefetch,
  } = useProgramList();

  const programFormModal = useProgramFormModal({
    refetch,
    listRefetch,
    listQueryVariables: { filters, pagination },
  });

  const handleToggleConfirm = async (record) => {
    try {
      await handleSwitchStatus(record, !record.isActive);
      notify.success({ message: t("customer.updateStatusSuccess") });
    } catch (err) {
      notify.error({ message: err?.message || t("loyaltyPackage.program.updateFailed") });
    }
  };

  const columns = [
    {
      title: t("common.index"),
      key: "index",
      width: 60,
      align: "center",
      render: (_, __, index) => pagination.offset + index + 1,
    },
    {
      title: t("loyaltyPackage.program.name"),
      dataIndex: "title",
      key: "title",
      render: (title, record) => (
        <Link to={`/loyalty-package/program/${record.id}`} className="loyalty-program-link">
          {title}
        </Link>
      ),
    },
    {
      title: t("loyaltyPackage.program.timePeriod"),
      key: "timePeriod",
      width: 200,
      render: (_, record) => {
        const range = formatLoyaltyDateRangeMultiline(record.startTime, record.endTime);
        if (typeof range === "string") return range;
        return (
          <span className="loyalty-date-range">
            {range.start} - <br /> {range.end}
          </span>
        );
      },
    },
    {
      title: t("loyaltyPackage.program.numOfPackages"),
      dataIndex: "numOfPeriods",
      key: "numOfPeriods",
      align: "center",
      width: 100,
    },
    {
      title: t("loyaltyPackage.program.status"),
      key: "status",
      align: "center",
      width: 140,
      render: (_, record) => TEXT_STATUS_LOYALTY[record.status] ?? record.status,
    },
    {
      title: t("loyaltyPackage.program.createdBy"),
      key: "createdBy",
      render: (_, record) => record.createdBy?.fullname ?? "—",
    },
    {
      title: t("loyaltyPackage.program.actions"),
      key: "actions",
      align: "center",
      fixed: "right",
      width: 160,
      className: "loyalty-action-column",
      render: (_, record) => (
        <LoyaltyTableActions
          isActive={record.isActive}
          switchLoading={switchLoading}
          onToggle={() => handleToggleConfirm(record)}
          confirmOnMessage={t("loyaltyPackage.program.confirmOn")}
          confirmOffMessage={t("loyaltyPackage.program.confirmOff")}
          showEdit={isEditable(record)}
          onEdit={() => programFormModal.openEdit(record)}
          editTooltip={t("common.edit")}
        />
      ),
    },
  ];

  const formInitialValues = programFormModal.pendingFormValues || programFormModal.getInitialFormValues();

  return (
    <div className="program-list-container">
      <LoyaltyListToolbar
        searchValue={searchTerm}
        onSearchChange={handleSearch}
        searchPlaceholder={t("loyaltyPackage.searchProgram")}
        createLabel={t("common.addProgram")}
        onCreate={programFormModal.openCreate}
      />

      <div className="custom-table-list-loyalty">
        <CustomTable
          columns={columns}
          dataSource={programs}
          rowKey="id"
          loading={loading}
          onChange={handleTableChange}
          scroll={{ x: "max-content" }}
          pagination={{
            total: paginationData.total,
            pageSize: pagination.limit,
            current: Math.floor(pagination.offset / pagination.limit) + 1,
            showSizeChanger: false,
          }}
        />
      </div>

      <ProgramFormModal
        key={programFormModal.formSessionKey}
        visible={programFormModal.isFormOpen}
        mode={programFormModal.formMode}
        initialValues={formInitialValues}
        onCancel={programFormModal.closeAll}
        onSubmit={programFormModal.submitForm}
      />

      <ProgramConfirmModal
        visible={programFormModal.isConfirmOpen}
        mode={programFormModal.formMode}
        data={programFormModal.pendingFormValues}
        loading={programFormModal.isSubmitting}
        onCancel={programFormModal.backToForm}
        onConfirm={programFormModal.confirmSubmit}
      />
    </div>
  );
}

export default ProgramList;
